export function clamp(n: number, min: number, max: number) {
  return Math.min(Math.max(n, min), max)
}

export type ContentRect = {
  left: number
  top: number
  width: number
  height: number
}

export type MapViewportOptions = {
  fitMode?: 'contain' | 'cover'
  /** Flip X when the output video is horizontally mirrored in CSS. */
  mirrored?: boolean
}

type MappingContext = {
  content: ContentRect
  /** Pointer hit-test bounds — inset media uses video rect; fill layout uses container. */
  hitRect: ContentRect
  source: 'media' | 'fit'
}

/** Visible video content box for contain/cover fit. */
export function getFitContentRect(
  rect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>,
  targetSize: [number, number],
  fitMode: 'contain' | 'cover' = 'contain',
): ContentRect {
  const [targetWidth, targetHeight] = targetSize
  if (!targetWidth || !targetHeight || !rect.width || !rect.height) {
    return {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    }
  }

  const scale =
    fitMode === 'cover'
      ? Math.max(rect.width / targetWidth, rect.height / targetHeight)
      : Math.min(rect.width / targetWidth, rect.height / targetHeight)
  const width = targetWidth * scale
  const height = targetHeight * scale

  return {
    left: rect.left + (rect.width - width) / 2,
    top: rect.top + (rect.height - height) / 2,
    width,
    height,
  }
}

export function findDragMediaElement(
  dragSurface: HTMLElement,
): HTMLVideoElement | HTMLCanvasElement | null {
  const root = dragSurface.parentElement ?? dragSurface
  if (typeof root.querySelector !== 'function') {
    return null
  }
  const videoHost = root.querySelector('[data-xmax-remote-video]') ?? root
  if (typeof videoHost.querySelector !== 'function') {
    return null
  }
  const media = videoHost.querySelector('video, canvas')
  return (media as HTMLVideoElement | HTMLCanvasElement | null) ?? null
}

function toContentRect(rect: DOMRect): ContentRect {
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  }
}

function isMediaInsetInContainer(mediaRect: DOMRect, containerRect: DOMRect): boolean {
  if (!mediaRect.width || !mediaRect.height || !containerRect.width || !containerRect.height) {
    return false
  }
  const widthRatio = mediaRect.width / containerRect.width
  const heightRatio = mediaRect.height / containerRect.height
  return widthRatio < 0.98 || heightRatio < 0.98
}

/**
 * Resolve drag mapping bounds.
 * - Upload / h-auto layouts: map to the actual output video element rect.
 * - Camera / cover fill: use container and cover math when video fills the host.
 */
export function resolveMappingContext(
  dragSurface: HTMLElement,
  targetSize: [number, number],
  fitMode: 'contain' | 'cover' = 'cover',
): MappingContext {
  const containerRect = dragSurface.getBoundingClientRect()
  const media = findDragMediaElement(dragSurface)
  if (media) {
    const mediaRect = media.getBoundingClientRect()
    if (mediaRect.width > 0 && mediaRect.height > 0 && isMediaInsetInContainer(mediaRect, containerRect)) {
      const content = toContentRect(mediaRect)
      return { content, hitRect: content, source: 'media' }
    }
  }

  const content = getFitContentRect(containerRect, targetSize, fitMode)
  return {
    content,
    hitRect: toContentRect(containerRect),
    source: 'fit',
  }
}

export function mapViewportPointToCoordinateSpace(
  e: Pick<PointerEvent, 'clientX' | 'clientY'>,
  container: HTMLElement,
  targetSize: [number, number],
  options: MapViewportOptions = {},
): [number, number] | null {
  const fitMode = options.fitMode ?? 'cover'
  const mirrored = options.mirrored ?? false
  const [targetWidth, targetHeight] = targetSize

  if (!targetWidth || !targetHeight) {
    return null
  }

  const { content, hitRect } = resolveMappingContext(container, targetSize, fitMode)
  if (!content.width || !content.height) {
    return null
  }

  if (
    e.clientX < hitRect.left
    || e.clientY < hitRect.top
    || e.clientX > hitRect.left + hitRect.width
    || e.clientY > hitRect.top + hitRect.height
  ) {
    return null
  }

  const x = Math.round(((e.clientX - content.left) * targetWidth) / content.width)
  const y = Math.round(((e.clientY - content.top) * targetHeight) / content.height)
  const normalizedX = mirrored ? targetWidth - 1 - x : x

  return [
    clamp(normalizedX, 0, targetWidth - 1),
    clamp(y, 0, targetHeight - 1),
  ]
}

/** Inverse of mapViewportPointToCoordinateSpace — target coords to canvas pixels (CSS space × dpr). */
export function mapTargetPointToCanvas(
  point: [number, number],
  container: HTMLElement,
  targetSize: [number, number],
  dpr: number,
  options: MapViewportOptions = {},
): [number, number] | null {
  const fitMode = options.fitMode ?? 'cover'
  const mirrored = options.mirrored ?? false
  const containerRect = container.getBoundingClientRect()
  const [targetWidth, targetHeight] = targetSize
  const { content } = resolveMappingContext(container, targetSize, fitMode)

  if (!targetWidth || !targetHeight || !content.width || !content.height) {
    return null
  }

  const displayX = mirrored ? targetWidth - 1 - point[0] : point[0]
  const originX = (content.left - containerRect.left) * dpr
  const originY = (content.top - containerRect.top) * dpr
  const contentW = content.width * dpr
  const contentH = content.height * dpr

  return [
    originX + (displayX / targetWidth) * contentW,
    originY + (point[1] / targetHeight) * contentH,
  ]
}
